import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { useAgentBranding } from "@/hooks/useAgentBranding";
import AgentBrandingCard from "@/components/agent/AgentBrandingCard";

const PAGE_TITLE = "Closing Costs in NJ (2026) | How Much, Who Pays & Complete Guide | Prime Realty Partners";
const PAGE_DESC =
  "What are closing costs in New Jersey? Learn how much buyers and sellers pay, average NJ closing costs, and ways to reduce them. Prime Realty Partners.";
const CANONICAL = "https://www.prpsj.com/closing-costs-nj";

const faqs = [
  {
    q: "How much are closing costs in NJ?",
    a: "For buyers, closing costs in NJ typically range from 2-5% of the purchase price. On a $400,000 home, expect $8,000-$20,000. For sellers, total costs typically run 4-8% of the sale price depending on commission structure.",
  },
  {
    q: "Who pays closing costs in NJ, buyer or seller?",
    a: "Both pay closing costs in NJ, but different ones. Buyers pay lender fees, title insurance, attorney fees, and prepaid taxes/insurance. Sellers pay agent commissions, NJ Realty Transfer Fee, and their attorney fees. Some costs are negotiable.",
  },
  {
    q: "Are closing costs tax deductible in NJ?",
    a: "Some closing costs are tax deductible, including mortgage interest, property taxes, and discount points paid to reduce your interest rate. Consult a tax professional for guidance specific to your situation.",
  },
  {
    q: "Can closing costs be rolled into the mortgage in NJ?",
    a: "In some cases, yes. FHA and VA loans allow certain closing costs to be financed. You can also use lender credits (accepting a higher rate to offset fees) or negotiate seller contributions. NJHMFA assistance can also be applied to closing costs.",
  },
  {
    q: "How do real estate commissions work in NJ after the NAR settlement?",
    a: "As of August 17, 2024, buyer-agent commissions can no longer be advertised on the MLS. Sellers may still choose to offer buyer-agent compensation, but it must be communicated outside the MLS. Buyers are now required to sign a written buyer representation agreement before touring homes, which clearly states how their agent will be compensated and for how much. Commission amounts remain fully negotiable and are not set by law. At Prime Realty Partners, we required buyer agreements long before the settlement — so for our buyers, the process has always been transparent from day one.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Closing Costs in New Jersey: Complete Guide (2026)",
  description: PAGE_DESC,
  url: CANONICAL,
};

const buyerCosts = [
  { item: "Loan origination fee", cost: "$1,000-$4,000 (0.25-1% of loan amount)" },
  { item: "Appraisal", cost: "$400-$600" },
  { item: "Credit report", cost: "$25-$75" },
  { item: "Title search", cost: "$200-$400" },
  { item: "Title insurance (lender's policy)", cost: "$800-$1,500" },
  { item: "Title insurance (owner's policy)", cost: "$1,000-$2,000 (optional but recommended)" },
  { item: "Attorney fee", cost: "$1,000-$2,500" },
  { item: "Recording fees", cost: "$200-$500" },
  { item: "Survey", cost: "$300-$700" },
  { item: "Homeowners insurance (prepaid)", cost: "$1,000-$2,500" },
  { item: "Property taxes (prepaid)", cost: "Varies significantly by town" },
  { item: "Flood certification", cost: "$15-$25" },
  { item: "NJ Realty Transfer Fee", cost: "Paid by seller in most cases" },
];

const ClosingCostsNJ = () => {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const { brandedAgent, isBranded } = useAgentBranding();

  return (
    <Layout>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESC}
        canonicalUrl={CANONICAL}
        schema={[faqSchema, webPageSchema]}
        keywords="closing costs NJ, how much are closing costs in New Jersey, who pays closing costs NJ, NJ buyer closing costs, NJ seller closing costs"
        noindex={isBranded}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Closing Costs NJ", url: CANONICAL }]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary/10 to-primary/5 py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileText className="h-4 w-4" />
              Buyer's Guide
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Closing Costs in New Jersey: Complete Guide (2026)
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              How much are closing costs in NJ, who pays them, and what you can do to reduce them.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-16">
          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">What Are Closing Costs?</h2>
            <p className="text-muted-foreground">
              Closing costs are fees and expenses beyond the purchase price of the home that buyers and sellers pay when
               a real estate transaction closes. They cover everything from lender processing to title insurance to
               government recording fees. In New Jersey, closing costs typically range from 2-5% of the purchase price
               for buyers and typically 4-8% for sellers depending on commission structure.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">How Much Are Closing Costs in NJ?</h2>
            <div className="bg-muted/50 rounded-lg p-6 mb-6 space-y-2">
              <p className="text-foreground">
                <strong>For buyers:</strong> 2-5% of the purchase price. On a $400,000 home, expect $8,000-$20,000 in
                closing costs.
              </p>
              <p className="text-foreground">
                <strong>For sellers:</strong> typically 4-8% of the sale price depending on commission structure, with agent
                commissions being the largest variable. On a $400,000 sale, expect $16,000-$32,000 total.
              </p>
            </div>
            <p className="text-muted-foreground mb-4">
              Here's a breakdown of typical buyer closing costs on a $400,000 NJ home:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-heading font-bold">Cost Item</th>
                    <th className="text-left py-3 px-4 font-heading font-bold">Typical Range</th>
                  </tr>
                </thead>
                <tbody>
                  {buyerCosts.map((row, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="py-3 px-4 text-muted-foreground">{row.item}</td>
                      <td className="py-3 px-4 text-muted-foreground">{row.cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Who Pays Closing Costs in NJ — Buyer or Seller?</h2>
            <p className="text-muted-foreground mb-4">
              In New Jersey, both buyers and sellers pay closing costs, but they pay different costs.
            </p>
            <p className="text-muted-foreground mb-4">
              <strong>Buyers typically pay:</strong> lender fees, appraisal, credit report, title insurance, attorney
              fees, recording fees, prepaid taxes and insurance, home inspection (paid separately before closing).
            </p>
            <p className="text-muted-foreground mb-4">
              <strong>Sellers typically pay:</strong> real estate agent commissions, NJ Realty Transfer Fee (tiered rate
              based on sale price, paid by seller in most cases), their own attorney fees, any negotiated repair
              credits, and outstanding liens or judgments.
            </p>
            <p className="text-muted-foreground">
              <strong>Key point:</strong> closing costs are negotiable. In a buyer's market, you may be able to
              negotiate for the seller to pay a portion of your closing costs. Your agent can advise on what's realistic
              in the current market.
            </p>
          </section>

          {/* Section 3b — NAR Settlement */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">
              Real Estate Commissions in NJ: What Changed in 2024
            </h2>

            <p className="text-muted-foreground mb-6">
              Real estate commissions are one of the largest costs in any transaction — and how they work changed
              significantly in August 2024 as a result of a landmark NAR settlement. Here's what buyers and sellers in
              New Jersey need to know.
            </p>

            <p className="text-muted-foreground mb-4">
              <strong>How commissions traditionally worked.</strong> For decades, the seller paid a total commission —
              typically 5-6% of the sale price — split between the listing agent and the buyer's agent. That buyer-agent
              compensation was advertised directly on the MLS, visible to every agent before they showed a home.
            </p>

            <p className="text-muted-foreground mb-4">
              <strong>What the NAR settlement changed.</strong> In March 2024, the National Association of Realtors®
              settled a federal antitrust lawsuit for $418 million. Effective August 17, 2024, two mandatory rules went
              into effect: first, buyer-agent compensation can no longer be advertised or offered on the MLS — any such
              offers must be communicated off-MLS directly between agents or parties. Second, agents must have a signed
              written buyer representation agreement in place before touring any home with a buyer. That agreement must
              clearly state the agent's compensation amount or rate and include a conspicuous disclosure that
              commissions are fully negotiable and not set by law.
            </p>

            <p className="text-muted-foreground mb-4">
              <strong>What this means for buyers.</strong> Before touring any home, you'll sign a buyer representation
              agreement that spells out exactly how your agent will be compensated and for how much. Sellers can still
              choose to offer buyer-agent compensation — and many do — but it's communicated off-MLS rather than listed
              publicly. If the seller doesn't offer it, buyers can negotiate it into the purchase price or pay their
              agent directly.
            </p>

            <p className="text-muted-foreground mb-4">
              <strong>A note on Prime Realty Partners.</strong> PRP required buyer representation agreements with all
              buyers well before the NAR settlement made it mandatory nationwide. This was never a change in practice
              for our agents — transparency around compensation has always been our standard. Buyers working with a PRP
              agent have always known exactly how their agent is compensated before any paperwork is signed.
            </p>

            <p className="text-muted-foreground mb-4">
              <strong>What this means for sellers.</strong> You now decide whether to offer buyer-agent compensation. If
              you do, your listing agent communicates it off-MLS to cooperating agents. Your listing agent's commission
              is negotiated separately. Offering buyer-agent compensation can still attract more qualified buyers —
              agents are less likely to show homes where their compensation isn't addressed — but it's your choice, not
              a requirement.
            </p>

            <p className="text-muted-foreground">
              <strong>The bottom line.</strong> Commissions are a real closing cost for both buyers and sellers. The
              settlement didn't eliminate them — it moved the conversation earlier in the process and made it more
              transparent. Your agent should be able to explain exactly how compensation is structured before you sign
              any agreements. If they can't, that's worth paying attention to.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Average Closing Costs in NJ by County</h2>
            <p className="text-muted-foreground mb-6">
              Closing costs vary by county because property taxes — one of the largest prepaid costs — differ
              significantly across South Jersey.
            </p>
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-6">
                <h3 className="font-heading text-xl font-bold mb-2">Camden County</h3>
                <p className="text-muted-foreground">
                  Property tax rates average around 3.5-4.0% of assessed value. Towns like Cherry Hill and Haddonfield
                  have higher taxes but strong schools.
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-6">
                <h3 className="font-heading text-xl font-bold mb-2">Burlington County</h3>
                <p className="text-muted-foreground">
                  Tax rates average around 2.5-3.5%. Towns like Moorestown and Mount Laurel offer moderate taxes
                  relative to home values.
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-6">
                <h3 className="font-heading text-xl font-bold mb-2">Gloucester County</h3>
                <p className="text-muted-foreground">
                  Tax rates average around 3.0-3.5%. Washington Township and Deptford offer good value.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Can Closing Costs Be Rolled Into a Mortgage?</h2>
            <p className="text-muted-foreground mb-4">In some cases, yes. Here are the options:</p>
            <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
              <li>
                <strong>FHA and VA loans</strong> allow you to finance some closing costs into the loan, though this
                increases your loan amount and monthly payments.
              </li>
              <li>
                <strong>Lender credits:</strong> You can choose a slightly higher interest rate in exchange for the
                lender covering some closing costs. This makes sense if you plan to refinance or sell within a few
                years.
              </li>
              <li>
                <strong>Seller contributions:</strong> The seller can agree to pay a portion of your closing costs as
                part of the negotiation. FHA allows up to 6% of the purchase price in seller-paid closing costs.
              </li>
              <li>
                <strong>NJHMFA down payment assistance</strong> can also be used toward closing costs, not just the down
                payment.
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">How to Reduce Your Closing Costs in NJ</h2>
            <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
              <li>
                <strong>Shop around for lenders</strong> — get quotes from at least 3 lenders and compare their Loan
                Estimates. The fees can vary significantly.
              </li>
              <li>
                <strong>Negotiate with the seller</strong> — especially in a slower market, sellers may agree to
                contribute toward your closing costs.
              </li>
              <li>
                <strong>Ask about lender credits</strong> — a slightly higher rate can offset thousands in upfront
                costs.
              </li>
              <li>
                <strong>Compare title companies</strong> — title insurance and settlement fees vary between providers.
                Your attorney can recommend options.
              </li>
              <li>
                <strong>Time your closing strategically</strong> — closing later in the month reduces the amount of
                prepaid interest you owe.
              </li>
            </ul>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group border border-border rounded-lg">
                  <summary className="flex items-center justify-between cursor-pointer p-4 font-medium text-foreground hover:bg-muted/50 rounded-lg">
                    {faq.q}
                    <span className="ml-2 transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="px-4 pb-4 text-muted-foreground">{faq.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* Explore Communities */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-4">Explore South Jersey Communities</h2>
            <p className="text-muted-foreground mb-6">Ready to start your search? Browse homes for sale across South Jersey's most popular communities.</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: "Cherry Hill", path: "/cherry-hill-homes-for-sale" },
                { name: "Moorestown", path: "/moorestown-homes-for-sale" },
                { name: "Mount Laurel", path: "/mount-laurel-homes-for-sale" },
                { name: "Marlton", path: "/marlton-homes-for-sale" },
                { name: "Voorhees", path: "/voorhees-homes-for-sale" },
                { name: "Haddonfield", path: "/haddonfield-homes-for-sale" },
                { name: "Washington Township", path: "/washington-township-homes-for-sale" },
                { name: "Gloucester Township", path: "/gloucester-township-homes-for-sale" },
                { name: "Deptford", path: "/deptford-homes-for-sale" },
                { name: "Cinnaminson", path: "/cinnaminson-homes-for-sale" },
                { name: "Medford", path: "/medford-homes-for-sale" },
                { name: "Collingswood", path: "/collingswood-homes-for-sale" },
              ].map((c) => (
                <Link key={c.name} to={c.path} className="inline-flex items-center px-3 py-1.5 rounded-full border border-border text-sm text-primary hover:bg-primary/5 transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Agent Branding */}
          {isBranded && brandedAgent && (
            <section>
              <AgentBrandingCard agent={brandedAgent} />
            </section>
          )}

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">
              Questions About Closing Costs on Your NJ Home Purchase?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              {isBranded && brandedAgent
                ? `${brandedAgent.display_name || "Your agent"} walks you through every cost before you get to the closing table.`
                : "Our agents walk you through every cost before you get to the closing table. No surprises."}
            </p>
            <Button asChild size="lg">
              <Link to={isBranded && brandedAgent?.slug ? `/agent/${brandedAgent.slug}` : "/contact"}>
                {isBranded && brandedAgent ? `Contact ${brandedAgent.display_name?.split(" ")[0] || "Agent"}` : "Get a Free Cost Estimate"}
              </Link>
            </Button>
          </section>
        </div>
      </article>
    </Layout>
  );
};

export default ClosingCostsNJ;
